/** 商品与类目管理 — Mock数据 */

import type {
  CategoryNode,
  ProductDetail,
  OriginTraceability,
  DestinationStandard,
  ProductTempColumns,
} from '../types/product';

// ===== 温控标准（7 列结构化，对应 DB products 新列；原 temp_standards JSONB 已弃用） =====
// 原 JSONB 三段（目标/预警/报警）已拆为数值列：
//   tempTarget / tempWarnMax(+tolerance) / tempAlarmMax(+tolerance)；
//   tempLowerLimit / tempLowerToleranceMin 为防冻下界（mock 中统一不设 = 不检查）。
// 容忍分钟由旧阈值串（如 "13°C / 2h"）换算为分钟。
const tempGarlic: ProductTempColumns = {
  tempTarget: -3, tempWarnMax: -1, tempWarnToleranceMin: 30,
  tempAlarmMax: 2, tempAlarmToleranceMin: 240,
  tempLowerLimit: null, tempLowerToleranceMin: null,
};
const tempGinger: ProductTempColumns = {
  tempTarget: 10, tempWarnMax: 13, tempWarnToleranceMin: 120,
  tempAlarmMax: 15, tempAlarmToleranceMin: 240,
  tempLowerLimit: null, tempLowerToleranceMin: null,
};
const tempOnion: ProductTempColumns = {
  tempTarget: 0, tempWarnMax: 3, tempWarnToleranceMin: 60,
  tempAlarmMax: 5, tempAlarmToleranceMin: 180,
  tempLowerLimit: null, tempLowerToleranceMin: null,
};
const tempPotato: ProductTempColumns = {
  tempTarget: 4, tempWarnMax: 8, tempWarnToleranceMin: 120,
  tempAlarmMax: 12, tempAlarmToleranceMin: 240,
  tempLowerLimit: null, tempLowerToleranceMin: null,
};
const tempGrape: ProductTempColumns = {
  tempTarget: 0, tempWarnMax: 2, tempWarnToleranceMin: 60,
  tempAlarmMax: 4, tempAlarmToleranceMin: 120,
  tempLowerLimit: null, tempLowerToleranceMin: null,
};
const tempCabbageRadish: ProductTempColumns = {
  tempTarget: 0, tempWarnMax: 3, tempWarnToleranceMin: 120,
  tempAlarmMax: 6, tempAlarmToleranceMin: 240,
  tempLowerLimit: null, tempLowerToleranceMin: null,
};
const tempChili: ProductTempColumns = {
  tempTarget: 15, tempWarnMax: 20, tempWarnToleranceMin: 120,
  tempAlarmMax: 25, tempAlarmToleranceMin: 240,
  tempLowerLimit: null, tempLowerToleranceMin: null,
};

// ===== 目的国标准 =====
const uaeDestStandards: DestinationStandard[] = [
  { country: 'UAE', standardName: 'MOCCAE 植检', standardType: 'phytosanitary' },
  { country: 'UAE', standardName: '食安证', standardType: 'food_safety' },
  { country: 'KSA', standardName: 'SASO', standardType: 'quality' },
  { country: 'SG', standardName: 'SFA', standardType: 'quality' },
];

const euDestStandards: DestinationStandard[] = [
  { country: 'EU', standardName: 'EC 178/2002', standardType: 'food_safety' },
  { country: 'EU', standardName: '植检证', standardType: 'phytosanitary' },
  { country: 'UAE', standardName: 'MOCCAE 植检', standardType: 'phytosanitary' },
  { country: 'KSA', standardName: 'SASO', standardType: 'quality' },
];

const asiaDestStandards: DestinationStandard[] = [
  { country: 'SG', standardName: 'SFA', standardType: 'quality' },
  { country: 'JP', standardName: 'JAS', standardType: 'quality' },
  { country: 'UAE', standardName: 'MOCCAE 植检', standardType: 'phytosanitary' },
];

// ===== 商品详情 + 产地溯源 =====

interface ProductData {
  product: ProductDetail;
  origin: OriginTraceability;
}

// ---------- 大蒜 (5 SKU) ----------
const garlicProducts: Record<string, ProductData> = {
  prod_garlic_jinxiang_55: {
    product: {
      id: 'prod_garlic_jinxiang_55',
      name: '山东金乡白皮大蒜 5.5cm+',
      productCode: 'GP-001',
      hsCode: '0703.2000',
      specification: '5.5cm+',
      packagingSpec: '10kg/箱',
      categoryId: 'cat_garlic',
      ...tempGarlic,
    },
    origin: {
      productId: 'prod_garlic_jinxiang_55',
      mainRegion: '山东省金乡县',
      harvestSeason: '6月-8月',
      farmRegNo: 'CN37-0001',
      factoryRegNo: '3700-F001',
      destStandards: uaeDestStandards,
    },
  },
  prod_garlic_jinxiang_50: {
    product: {
      id: 'prod_garlic_jinxiang_50',
      name: '山东金乡紫皮大蒜 5.0cm+',
      productCode: 'GP-002',
      hsCode: '0703.2000',
      specification: '5.0cm+',
      packagingSpec: '10kg/箱',
      categoryId: 'cat_garlic',
      ...tempGarlic,
    },
    origin: {
      productId: 'prod_garlic_jinxiang_50',
      mainRegion: '山东省金乡县',
      harvestSeason: '6月-8月',
      farmRegNo: 'CN37-0002',
      factoryRegNo: '3700-F001',
      destStandards: euDestStandards,
    },
  },
  prod_garlic_qixian_45: {
    product: {
      id: 'prod_garlic_qixian_45',
      name: '河南杞县白皮大蒜 4.5cm+',
      productCode: 'GP-003',
      hsCode: '0703.2000',
      specification: '4.5cm+',
      packagingSpec: '10kg/箱',
      categoryId: 'cat_garlic',
      ...tempGarlic,
    },
    origin: {
      productId: 'prod_garlic_qixian_45',
      mainRegion: '河南省杞县',
      harvestSeason: '6月-7月',
      farmRegNo: 'CN41-0001',
      factoryRegNo: '4100-F002',
      destStandards: uaeDestStandards,
    },
  },
  prod_garlic_dali_60: {
    product: {
      id: 'prod_garlic_dali_60',
      name: '云南大理紫皮大蒜 6.0cm+',
      productCode: 'GP-004',
      hsCode: '0703.2000',
      specification: '6.0cm+',
      packagingSpec: '10kg/箱',
      categoryId: 'cat_garlic',
      ...tempGarlic,
    },
    origin: {
      productId: 'prod_garlic_dali_60',
      mainRegion: '云南省大理州',
      harvestSeason: '4月-5月',
      farmRegNo: 'CN53-0001',
      factoryRegNo: '5300-F003',
      destStandards: asiaDestStandards,
    },
  },
  prod_garlic_pizhou_50: {
    product: {
      id: 'prod_garlic_pizhou_50',
      name: '江苏邳州白皮大蒜 5.0cm+',
      productCode: 'GP-005',
      hsCode: '0703.2000',
      specification: '5.0cm+',
      packagingSpec: '10kg/箱',
      categoryId: 'cat_garlic',
      ...tempGarlic,
    },
    origin: {
      productId: 'prod_garlic_pizhou_50',
      mainRegion: '江苏省邳州市',
      harvestSeason: '5月-7月',
      farmRegNo: 'CN32-0001',
      factoryRegNo: '3200-F004',
      destStandards: uaeDestStandards,
    },
  },
};

// ---------- 大姜 (3 SKU) ----------
const gingerProducts: Record<string, ProductData> = {
  prod_ginger_anqiu: {
    product: {
      id: 'prod_ginger_anqiu',
      name: '山东安丘大姜 200g+',
      productCode: 'GP-006',
      hsCode: '0910.1100',
      specification: '200g+',
      packagingSpec: '20kg/箱',
      categoryId: 'cat_ginger',
      ...tempGinger,
    },
    origin: {
      productId: 'prod_ginger_anqiu',
      mainRegion: '山东省安丘市',
      harvestSeason: '10月-11月',
      farmRegNo: 'CN37-0010',
      factoryRegNo: '3700-F010',
      destStandards: [
        { country: 'UAE', standardName: 'MOCCAE 植检', standardType: 'phytosanitary' },
        { country: 'UAE', standardName: '食安证', standardType: 'food_safety' },
        { country: 'KSA', standardName: 'SASO', standardType: 'quality' },
      ],
    },
  },
  prod_ginger_changyi: {
    product: {
      id: 'prod_ginger_changyi',
      name: '山东昌邑大姜 250g+',
      productCode: 'GP-007',
      hsCode: '0910.1100',
      specification: '250g+',
      packagingSpec: '20kg/箱',
      categoryId: 'cat_ginger',
      ...tempGinger,
    },
    origin: {
      productId: 'prod_ginger_changyi',
      mainRegion: '山东省昌邑市',
      harvestSeason: '10月-11月',
      farmRegNo: 'CN37-0011',
      factoryRegNo: '3700-F010',
      destStandards: [
        { country: 'EU', standardName: 'EC 178/2002', standardType: 'food_safety' },
        { country: 'SG', standardName: 'SFA', standardType: 'quality' },
      ],
    },
  },
  prod_ginger_luoping: {
    product: {
      id: 'prod_ginger_luoping',
      name: '云南罗平小黄姜 150g+',
      productCode: 'GP-008',
      hsCode: '0910.1100',
      specification: '150g+',
      packagingSpec: '15kg/箱',
      categoryId: 'cat_ginger',
      ...tempGinger,
    },
    origin: {
      productId: 'prod_ginger_luoping',
      mainRegion: '云南省罗平县',
      harvestSeason: '9月-10月',
      farmRegNo: 'CN53-0010',
      factoryRegNo: '5300-F010',
      destStandards: asiaDestStandards,
    },
  },
};

// ---------- 洋葱 (2 SKU) ----------
const onionProducts: Record<string, ProductData> = {
  prod_onion_jiuquan: {
    product: {
      id: 'prod_onion_jiuquan',
      name: '甘肃酒泉黄皮洋葱 80mm+',
      productCode: 'GP-009',
      hsCode: '0703.1010',
      specification: '80mm+',
      packagingSpec: '25kg/袋',
      categoryId: 'cat_onion',
      ...tempOnion,
    },
    origin: {
      productId: 'prod_onion_jiuquan',
      mainRegion: '甘肃省酒泉市',
      harvestSeason: '8月-9月',
      farmRegNo: 'CN62-0001',
      factoryRegNo: '6200-F001',
      destStandards: uaeDestStandards,
    },
  },
  prod_onion_yuanmou: {
    product: {
      id: 'prod_onion_yuanmou',
      name: '云南元谋紫皮洋葱 70mm+',
      productCode: 'GP-010',
      hsCode: '0703.1010',
      specification: '70mm+',
      packagingSpec: '25kg/袋',
      categoryId: 'cat_onion',
      ...tempOnion,
    },
    origin: {
      productId: 'prod_onion_yuanmou',
      mainRegion: '云南省元谋县',
      harvestSeason: '3月-4月',
      farmRegNo: 'CN53-0020',
      factoryRegNo: '5300-F020',
      destStandards: asiaDestStandards,
    },
  },
};

// ---------- 土豆 (2 SKU) ----------
const potatoProducts: Record<string, ProductData> = {
  prod_potato_wulanchabu: {
    product: {
      id: 'prod_potato_wulanchabu',
      name: '内蒙古乌兰察布土豆 150g+',
      productCode: 'GP-011',
      hsCode: '0701.9000',
      specification: '150g+',
      packagingSpec: '25kg/袋',
      categoryId: 'cat_potato',
      ...tempPotato,
    },
    origin: {
      productId: 'prod_potato_wulanchabu',
      mainRegion: '内蒙古乌兰察布市',
      harvestSeason: '9月-10月',
      farmRegNo: 'CN15-0001',
      factoryRegNo: '1500-F001',
      destStandards: [
        { country: 'UAE', standardName: 'MOCCAE 植检', standardType: 'phytosanitary' },
        { country: 'KSA', standardName: 'SASO', standardType: 'quality' },
      ],
    },
  },
  prod_potato_dingxi: {
    product: {
      id: 'prod_potato_dingxi',
      name: '甘肃定西土豆 200g+',
      productCode: 'GP-012',
      hsCode: '0701.9000',
      specification: '200g+',
      packagingSpec: '25kg/袋',
      categoryId: 'cat_potato',
      ...tempPotato,
    },
    origin: {
      productId: 'prod_potato_dingxi',
      mainRegion: '甘肃省定西市',
      harvestSeason: '9月-10月',
      farmRegNo: 'CN62-0010',
      factoryRegNo: '6200-F010',
      destStandards: euDestStandards,
    },
  },
};

// ---------- 苹果 (3 SKU) ----------
const appleProducts: Record<string, ProductData> = {
  prod_apple_yantai: {
    product: {
      id: 'prod_apple_yantai',
      name: '山东烟台红富士 80#',
      productCode: 'GP-013',
      hsCode: '0808.1000',
      specification: '80#',
      packagingSpec: '18kg/箱',
      categoryId: 'cat_apple',
      ...tempGarlic,
    },
    origin: {
      productId: 'prod_apple_yantai',
      mainRegion: '山东省烟台市',
      harvestSeason: '10月-11月',
      farmRegNo: 'CN37-0100',
      factoryRegNo: '3700-F100',
      destStandards: euDestStandards,
    },
  },
  prod_apple_luochuan: {
    product: {
      id: 'prod_apple_luochuan',
      name: '陕西洛川红富士 85#',
      productCode: 'GP-014',
      hsCode: '0808.1000',
      specification: '85#',
      packagingSpec: '18kg/箱',
      categoryId: 'cat_apple',
      ...tempGarlic,
    },
    origin: {
      productId: 'prod_apple_luochuan',
      mainRegion: '陕西省洛川县',
      harvestSeason: '10月-11月',
      farmRegNo: 'CN61-0100',
      factoryRegNo: '6100-F100',
      destStandards: uaeDestStandards,
    },
  },
  prod_apple_akesu: {
    product: {
      id: 'prod_apple_akesu',
      name: '新疆阿克苏冰糖心 80#',
      productCode: 'GP-015',
      hsCode: '0808.1000',
      specification: '80#',
      packagingSpec: '15kg/箱',
      categoryId: 'cat_apple',
      ...tempGarlic,
    },
    origin: {
      productId: 'prod_apple_akesu',
      mainRegion: '新疆阿克苏地区',
      harvestSeason: '10月-11月',
      farmRegNo: 'CN65-0100',
      factoryRegNo: '6500-F100',
      destStandards: asiaDestStandards,
    },
  },
};

// ---------- 梨 (3 SKU) ----------
const pearProducts: Record<string, ProductData> = {
  prod_pear_zhaoxian: {
    product: {
      id: 'prod_pear_zhaoxian',
      name: '河北赵县雪花梨 300g+',
      productCode: 'GP-016',
      hsCode: '0808.3000',
      specification: '300g+',
      packagingSpec: '15kg/箱',
      categoryId: 'cat_pear',
      ...tempGarlic,
    },
    origin: {
      productId: 'prod_pear_zhaoxian',
      mainRegion: '河北省赵县',
      harvestSeason: '9月-10月',
      farmRegNo: 'CN13-0100',
      factoryRegNo: '1300-F100',
      destStandards: [
        { country: 'UAE', standardName: 'MOCCAE 植检', standardType: 'phytosanitary' },
        { country: 'SG', standardName: 'SFA', standardType: 'quality' },
      ],
    },
  },
  prod_pear_dangshan: {
    product: {
      id: 'prod_pear_dangshan',
      name: '安徽砀山酥梨 250g+',
      productCode: 'GP-017',
      hsCode: '0808.3000',
      specification: '250g+',
      packagingSpec: '15kg/箱',
      categoryId: 'cat_pear',
      ...tempGarlic,
    },
    origin: {
      productId: 'prod_pear_dangshan',
      mainRegion: '安徽省砀山县',
      harvestSeason: '9月-10月',
      farmRegNo: 'CN34-0100',
      factoryRegNo: '3400-F100',
      destStandards: euDestStandards,
    },
  },
  prod_pear_kuerle: {
    product: {
      id: 'prod_pear_kuerle',
      name: '新疆库尔勒香梨 120g+',
      productCode: 'GP-018',
      hsCode: '0808.3000',
      specification: '120g+',
      packagingSpec: '10kg/箱',
      categoryId: 'cat_pear',
      ...tempGarlic,
    },
    origin: {
      productId: 'prod_pear_kuerle',
      mainRegion: '新疆库尔勒市',
      harvestSeason: '9月-10月',
      farmRegNo: 'CN65-0200',
      factoryRegNo: '6500-F200',
      destStandards: asiaDestStandards,
    },
  },
};

// ---------- 葡萄 (2 SKU) ----------
const grapeProducts: Record<string, ProductData> = {
  prod_grape_tulufan: {
    product: {
      id: 'prod_grape_tulufan',
      name: '新疆吐鲁番无核白 500g串',
      productCode: 'GP-019',
      hsCode: '0806.1000',
      specification: '500g/串',
      packagingSpec: '10kg/箱',
      categoryId: 'cat_grape',
      ...tempGrape,
    },
    origin: {
      productId: 'prod_grape_tulufan',
      mainRegion: '新疆吐鲁番市',
      harvestSeason: '8月-9月',
      farmRegNo: 'CN65-0300',
      factoryRegNo: '6500-F300',
      destStandards: [
        { country: 'SG', standardName: 'SFA', standardType: 'quality' },
        { country: 'UAE', standardName: 'MOCCAE 植检', standardType: 'phytosanitary' },
      ],
    },
  },
  prod_grape_huailai: {
    product: {
      id: 'prod_grape_huailai',
      name: '河北怀来龙眼葡萄 400g串',
      productCode: 'GP-020',
      hsCode: '0806.1000',
      specification: '400g/串',
      packagingSpec: '10kg/箱',
      categoryId: 'cat_grape',
      ...tempGrape,
    },
    origin: {
      productId: 'prod_grape_huailai',
      mainRegion: '河北省怀来县',
      harvestSeason: '9月-10月',
      farmRegNo: 'CN13-0200',
      factoryRegNo: '1300-F200',
      destStandards: [
        { country: 'UAE', standardName: 'MOCCAE 植检', standardType: 'phytosanitary' },
        { country: 'KSA', standardName: 'SASO', standardType: 'quality' },
      ],
    },
  },
};

// ---------- 白菜 (2 SKU) ----------
const cabbageProducts: Record<string, ProductData> = {
  prod_cabbage_jiaozhou: {
    product: {
      id: 'prod_cabbage_jiaozhou',
      name: '山东胶州大白菜 2.5kg+',
      productCode: 'GP-021',
      hsCode: '0704.9000',
      specification: '2.5kg+/颗',
      packagingSpec: '20kg/箱',
      categoryId: 'cat_cabbage',
      ...tempCabbageRadish,
    },
    origin: {
      productId: 'prod_cabbage_jiaozhou',
      mainRegion: '山东省胶州市',
      harvestSeason: '11月-12月',
      farmRegNo: 'CN37-0300',
      factoryRegNo: '3700-F300',
      destStandards: [
        { country: 'SG', standardName: 'SFA', standardType: 'quality' },
        { country: 'UAE', standardName: 'MOCCAE 植检', standardType: 'phytosanitary' },
      ],
    },
  },
  prod_cabbage_yutian: {
    product: {
      id: 'prod_cabbage_yutian',
      name: '河北玉田包尖白菜 2.0kg+',
      productCode: 'GP-022',
      hsCode: '0704.9000',
      specification: '2.0kg+/颗',
      packagingSpec: '20kg/箱',
      categoryId: 'cat_cabbage',
      ...tempCabbageRadish,
    },
    origin: {
      productId: 'prod_cabbage_yutian',
      mainRegion: '河北省玉田县',
      harvestSeason: '10月-11月',
      farmRegNo: 'CN13-0300',
      factoryRegNo: '1300-F300',
      destStandards: [
        { country: 'KSA', standardName: 'SASO', standardType: 'quality' },
        { country: 'UAE', standardName: 'MOCCAE 植检', standardType: 'phytosanitary' },
      ],
    },
  },
};

// ---------- 萝卜 (2 SKU) ----------
const radishProducts: Record<string, ProductData> = {
  prod_radish_weifang: {
    product: {
      id: 'prod_radish_weifang',
      name: '山东潍坊青萝卜 500g+',
      productCode: 'GP-023',
      hsCode: '0706.9000',
      specification: '500g+/根',
      packagingSpec: '15kg/箱',
      categoryId: 'cat_radish',
      ...tempCabbageRadish,
    },
    origin: {
      productId: 'prod_radish_weifang',
      mainRegion: '山东省潍坊市',
      harvestSeason: '10月-11月',
      farmRegNo: 'CN37-0400',
      factoryRegNo: '3700-F400',
      destStandards: [
        { country: 'UAE', standardName: 'MOCCAE 植检', standardType: 'phytosanitary' },
        { country: 'SG', standardName: 'SFA', standardType: 'quality' },
      ],
    },
  },
  prod_radish_tianjin: {
    product: {
      id: 'prod_radish_tianjin',
      name: '天津沙窝萝卜 400g+',
      productCode: 'GP-024',
      hsCode: '0706.9000',
      specification: '400g+/根',
      packagingSpec: '15kg/箱',
      categoryId: 'cat_radish',
      ...tempCabbageRadish,
    },
    origin: {
      productId: 'prod_radish_tianjin',
      mainRegion: '天津市西青区',
      harvestSeason: '10月-11月',
      farmRegNo: 'CN12-0400',
      factoryRegNo: '1200-F400',
      destStandards: [
        { country: 'KSA', standardName: 'SASO', standardType: 'quality' },
        { country: 'SG', standardName: 'SFA', standardType: 'quality' },
      ],
    },
  },
};

// ---------- 辣椒 (2 SKU) ----------
const chiliProducts: Record<string, ProductData> = {
  prod_chili_zunyi: {
    product: {
      id: 'prod_chili_zunyi',
      name: '贵州遵义朝天椒 干椒',
      productCode: 'GP-025',
      hsCode: '0904.2100',
      specification: '干椒',
      packagingSpec: '25kg/袋',
      categoryId: 'cat_chili',
      ...tempChili,
    },
    origin: {
      productId: 'prod_chili_zunyi',
      mainRegion: '贵州省遵义市',
      harvestSeason: '8月-10月',
      farmRegNo: 'CN52-0500',
      factoryRegNo: '5200-F500',
      destStandards: [
        { country: 'UAE', standardName: 'MOCCAE 植检', standardType: 'phytosanitary' },
        { country: 'UAE', standardName: '食安证', standardType: 'food_safety' },
      ],
    },
  },
  prod_chili_zhecheng: {
    product: {
      id: 'prod_chili_zhecheng',
      name: '河南柘城三樱椒 干椒',
      productCode: 'GP-026',
      hsCode: '0904.2100',
      specification: '干椒',
      packagingSpec: '25kg/袋',
      categoryId: 'cat_chili',
      ...tempChili,
    },
    origin: {
      productId: 'prod_chili_zhecheng',
      mainRegion: '河南省柘城县',
      harvestSeason: '9月-10月',
      farmRegNo: 'CN41-0500',
      factoryRegNo: '4100-F500',
      destStandards: [
        { country: 'KSA', standardName: 'SASO', standardType: 'quality' },
        { country: 'SG', standardName: 'SFA', standardType: 'quality' },
      ],
    },
  },
};

// ===== 汇总 Map =====

/** 所有商品详情 Map（keyed by productId） */
export const mockProductDetailMap: Record<string, ProductDetail> = {};
/** 所有产地溯源 Map（keyed by productId） */
export const mockOriginMap: Record<string, OriginTraceability> = {};

const allProductGroups: Record<string, ProductData>[] = [
  garlicProducts, gingerProducts, onionProducts, potatoProducts,
  appleProducts, pearProducts, grapeProducts,
  cabbageProducts, radishProducts, chiliProducts,
];

for (const group of allProductGroups) {
  for (const [id, data] of Object.entries(group)) {
    mockProductDetailMap[id] = data.product;
    mockOriginMap[id] = data.origin;
  }
}

// ===== 分类树 =====

function leafNode(id: string, name: string, icon: string, productId: string): CategoryNode {
  return { nodeType: 'product', id, name, icon, skuCount: 1, productId, children: [] };
}

function parentNode(id: string, name: string, icon: string, children: CategoryNode[]): CategoryNode {
  const skuCount = children.reduce((sum, c) => sum + (c.skuCount ?? 0), 0);
  return { nodeType: 'category', id, name, icon, skuCount, children };
}

const garlicChildren: CategoryNode[] = [
  leafNode('cat_garlic_jinxiang_55', '山东金乡白皮大蒜 5.5cm+', '🧄', 'prod_garlic_jinxiang_55'),
  leafNode('cat_garlic_jinxiang_50', '山东金乡紫皮大蒜 5.0cm+', '🧄', 'prod_garlic_jinxiang_50'),
  leafNode('cat_garlic_qixian_45', '河南杞县白皮大蒜 4.5cm+', '🧄', 'prod_garlic_qixian_45'),
  leafNode('cat_garlic_dali_60', '云南大理紫皮大蒜 6.0cm+', '🧄', 'prod_garlic_dali_60'),
  leafNode('cat_garlic_pizhou_50', '江苏邳州白皮大蒜 5.0cm+', '🧄', 'prod_garlic_pizhou_50'),
];

const gingerChildren: CategoryNode[] = [
  leafNode('cat_ginger_anqiu', '山东安丘大姜 200g+', '🌿', 'prod_ginger_anqiu'),
  leafNode('cat_ginger_changyi', '山东昌邑大姜 250g+', '🌿', 'prod_ginger_changyi'),
  leafNode('cat_ginger_luoping', '云南罗平小黄姜 150g+', '🌿', 'prod_ginger_luoping'),
];

const onionChildren: CategoryNode[] = [
  leafNode('cat_onion_jiuquan', '甘肃酒泉黄皮洋葱 80mm+', '🧅', 'prod_onion_jiuquan'),
  leafNode('cat_onion_yuanmou', '云南元谋紫皮洋葱 70mm+', '🧅', 'prod_onion_yuanmou'),
];

const potatoChildren: CategoryNode[] = [
  leafNode('cat_potato_wulanchabu', '内蒙古乌兰察布土豆 150g+', '🥔', 'prod_potato_wulanchabu'),
  leafNode('cat_potato_dingxi', '甘肃定西土豆 200g+', '🥔', 'prod_potato_dingxi'),
];

const appleChildren: CategoryNode[] = [
  leafNode('cat_apple_yantai', '山东烟台红富士 80#', '🍎', 'prod_apple_yantai'),
  leafNode('cat_apple_luochuan', '陕西洛川红富士 85#', '🍎', 'prod_apple_luochuan'),
  leafNode('cat_apple_akesu', '新疆阿克苏冰糖心 80#', '🍎', 'prod_apple_akesu'),
];

const pearChildren: CategoryNode[] = [
  leafNode('cat_pear_zhaoxian', '河北赵县雪花梨 300g+', '🍐', 'prod_pear_zhaoxian'),
  leafNode('cat_pear_dangshan', '安徽砀山酥梨 250g+', '🍐', 'prod_pear_dangshan'),
  leafNode('cat_pear_kuerle', '新疆库尔勒香梨 120g+', '🍐', 'prod_pear_kuerle'),
];

const grapeChildren: CategoryNode[] = [
  leafNode('cat_grape_tulufan', '新疆吐鲁番无核白 500g串', '🍇', 'prod_grape_tulufan'),
  leafNode('cat_grape_huailai', '河北怀来龙眼葡萄 400g串', '🍇', 'prod_grape_huailai'),
];

const cabbageChildren: CategoryNode[] = [
  leafNode('cat_cabbage_jiaozhou', '山东胶州大白菜 2.5kg+', '🥬', 'prod_cabbage_jiaozhou'),
  leafNode('cat_cabbage_yutian', '河北玉田包尖白菜 2.0kg+', '🥬', 'prod_cabbage_yutian'),
];

const radishChildren: CategoryNode[] = [
  leafNode('cat_radish_weifang', '山东潍坊青萝卜 500g+', '🥕', 'prod_radish_weifang'),
  leafNode('cat_radish_tianjin', '天津沙窝萝卜 400g+', '🥕', 'prod_radish_tianjin'),
];

const chiliChildren: CategoryNode[] = [
  leafNode('cat_chili_zunyi', '贵州遵义朝天椒 干椒', '🌶️', 'prod_chili_zunyi'),
  leafNode('cat_chili_zhecheng', '河南柘城三樱椒 干椒', '🌶️', 'prod_chili_zhecheng'),
];

const rootVegetableChildren: CategoryNode[] = [
  parentNode('cat_garlic', '大蒜', '🧄', garlicChildren),
  parentNode('cat_ginger', '大姜', '🌿', gingerChildren),
  parentNode('cat_onion', '洋葱', '🧅', onionChildren),
  parentNode('cat_potato', '土豆', '🥔', potatoChildren),
];

const fruitChildren: CategoryNode[] = [
  parentNode('cat_apple', '苹果', '🍎', appleChildren),
  parentNode('cat_pear', '梨', '🍐', pearChildren),
  parentNode('cat_grape', '葡萄', '🍇', grapeChildren),
];

const vegetableChildren: CategoryNode[] = [
  parentNode('cat_cabbage', '白菜', '🥬', cabbageChildren),
  parentNode('cat_radish', '萝卜', '🥕', radishChildren),
  parentNode('cat_chili', '辣椒', '🌶️', chiliChildren),
];

/** 完整三级分类树 */
export const mockCategoryTree: CategoryNode[] = [
  parentNode('cat_root_vegetable', '根茎类', '📁', rootVegetableChildren),
  parentNode('cat_fruit', '水果类', '📁', fruitChildren),
  parentNode('cat_vegetable', '蔬菜类', '📁', vegetableChildren),
];
